import React, { useState, useMemo } from "react";
//updated Halimunnisa 2026-06-08
//Implemented a comprehensive export functionality for sales that allows users to easily export their sales data to Excel. The export includes robust data mapping and formatting to ensure that the exported file is well-structured and user-friendly, making it easier for users to analyze and share their sales information outside of the application. The export process also includes error handling and user feedback to enhance the overall experience when exporting sales records.
import toast from "react-hot-toast";
import { ShoppingBag } from "lucide-react";
import { SalesTable } from "./SalesTable";
import { SalePaymentModal } from "./SalePaymentModal";
import { FilterSection } from "../../../components/ui";
import html2pdf from 'html2pdf.js';
import InvoiceSale from '../../../utils/InvoiceSale';
import { useCompanyInfo } from '../../verve-settings/hooks/useCompanyInfo';
import api from "../../../services/api";

// ---------------------------------------------------------------------------
// Helper: check if a date falls within local filter range
// ---------------------------------------------------------------------------
const isDateInRange = (dateInput, localFilters) => {
  if (!dateInput) return true;
  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return true;
  const dS = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

  if (localFilters.fromDate) {
    const f = new Date(localFilters.fromDate);
    if (dS < new Date(f.getFullYear(), f.getMonth(), f.getDate()).getTime()) return false;
  }
  if (localFilters.toDate) {
    const t = new Date(localFilters.toDate);
    if (dS > new Date(t.getFullYear(), t.getMonth(), t.getDate()).getTime()) return false;
  }
  if (localFilters.date && localFilters.date !== 'all') {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    if (localFilters.date === 'today') return dS === today;
    if (localFilters.date === 'this_week') {
      const start = new Date(today); start.setDate(start.getDate() - 6);
      return dS >= start.getTime();
    }
    if (localFilters.date === 'this_month') {
      const start = new Date(today); start.setDate(start.getDate() - 30);
      return dS >= start.getTime();
    }
    if (localFilters.date === 'this_year') {
      const start = new Date(today); start.setDate(start.getDate() - 364);
      return dS >= start.getTime();
    }
  }
  return true;
};

export const SalesView = ({
  dynamicSettings,
  activeTab,
  allSales = [],
  setSelReq,
  setSelSale,
  setSaleOpen,
  refreshAll
}) => {
  // ── Isolated local state ────────────────────────────────────────────────
  const [localSearch, setLocalSearch] = useState('');
  const [localFilters, setLocalFilters] = useState({});
  const [downloadingSale, setDownloadingSale] = useState(null);
  const [paymentSale, setPaymentSale] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const { company } = useCompanyInfo();

  const handleFilterChange = (key, val) =>
    setLocalFilters(prev => ({ ...prev, [key]: val }));

  // ── Local filtering logic ───────────────────────────────────────────────
  const filteredSales = useMemo(() => {
    const term = localSearch.toLowerCase();

    return (allSales || [])
      .filter(s => {
        const isProject = s.recordType === 'material_request';
        const proj = s.project || s.originalRequest?.project || {};
        const customerName = isProject
          ? (proj.customerName || s.customer || 'Project Site')
          : (typeof s.customer === 'object' ? (s.customer?.name || '') : (s.customer || ''));

        const invoiceNo = s.invoiceNo || '';
        const items = s.items || [];
        const itemSummary = items.map(it => {
          const name = it.itemName || it.item?.name || '';
          const q = it.deliveredQty || it.qty || 0;
          return name ? `${name} (x${q})` : '';
        }).filter(Boolean).join(', ');

        const searchMatch = !term ||
          customerName.toLowerCase().includes(term) ||
          String(invoiceNo).toLowerCase().includes(term) ||
          itemSummary.toLowerCase().includes(term);

        const dateMatch = isDateInRange(s.date || s.createdAt, localFilters);

        // Sale type filter: 'retail' | 'project' | 'all'
        const typeFilter = String(localFilters.saleType || 'all').toLowerCase();
        const typeMatch = typeFilter === 'all' ||
          (typeFilter === 'retail' && !isProject) ||
          (typeFilter === 'project' && isProject);

        return searchMatch && dateMatch && typeMatch;
      })
      .map(s => {
        const isProject = s.recordType === 'material_request';
        const proj = s.project || s.originalRequest?.project || {};
        const items = s.items || [];
        const customerName = isProject
          ? (proj.customerName || s.customer || 'Project Site')
          : (typeof s.customer === 'object' ? (s.customer?.name || '') : (s.customer || ''));

        const itemSummary = items.map(it => {
          const name = it.itemName || it.item?.description || it.item?.itemName || it.item?.articleId || it.item?.name || '';
          const q = it.deliveredQty || it.qty || 0;
          return name ? `${name} (x${q})` : '';
        }).filter(Boolean).join(', ');

        const calculatedNet = items.reduce((sum, it) => {
          return sum + (Number(it.deliveredQty || it.qty || 0) * Number(it.rate || it.unitPrice || 0));
        }, 0);

        return {
          ...s,
          recordType: isProject ? 'material_request' : 'sale',
          invoiceNo: isProject ? (s.invoiceNo || s.requestNo || '—') : (s.invoiceNo || '—'),
          customer: customerName,
          projectId: isProject ? (proj.projectId || s.projectId || '') : (s.invoiceNo || ''),
          itemSummary,
          totalQty: items.reduce((sum, it) => sum + (Number(it.deliveredQty || it.qty) || 0), 0),
          netPayable: s.netPayable || s.totalAmount || calculatedNet,
          status: s.status || 'Paid',
          displayStatus: s.status
        };
      })
      .sort((a, b) => new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt));
  }, [allSales, localSearch, localFilters]);

  // ── PDF download ────────────────────────────────────────────────────────
  const handleDownload = async (sale) => {
    try {
      const res = await api.get('/venus-billing/banks');
      const bankData = res.data?.data?.accounts?.[0] || res.data?.accounts?.[0] || null;
      setDownloadingSale({ ...sale, bankData });
    } catch (err) {
      console.error("Failed to fetch bank data for invoice", err);
      setDownloadingSale(sale);
    }
    
    setTimeout(() => {
      const element = document.getElementById('invoice-pdf-content');
      if (!element) { setDownloadingSale(null); return; }
      const opt = {
        margin: [5, 5, 5, 5],
        filename: `Invoice_${sale.invoiceNo || 'Sale'}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, letterRendering: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };
      html2pdf().from(element).set(opt).save().then(() => setDownloadingSale(null));
    }, 500);
  };
//updated Halimunnisa 2026-06-08
// Implemented a comprehensive export functionality for sales that allows users to easily export their sales data to Excel. The export includes robust data mapping and formatting to ensure that the exported file is well-structured and user-friendly, making it easier for users to analyze and share their sales information outside of the application. The export process also includes error handling and user feedback to enhance the overall experience when exporting sales records.
  // ── Excel Export ─────────────────────────────────────────────────────────
  const handleExport = async () => {
    if (isExporting) return;
    try {
      setIsExporting(true);
      const exportSales = filteredSales || [];

      if (exportSales.length === 0) {
        toast.error("No sales to export");
        return;
      }

      const { exportToExcel } = await import("../../../utils/exportUtils");
      
      const exportData = exportSales.map(s => {
        return {
          'Type': s.recordType === 'material_request' ? 'Project' : 'Retail',
          'Ref #': s.invoiceNo || '—',
          'Date': s.date || s.createdAt ? new Date(s.date || s.createdAt).toLocaleDateString() : '—',
          'Customer / Project Name': s.customer || '—',
          'Project ID': s.recordType === 'material_request' ? (s.projectId || '—') : '—',
          'Customer Email': s.customerEmail || '—',
          'Customer Phone': s.customerPhone || '—',
          'Billing Address': s.billingAddressLine1 ? [s.billingAddressLine1, s.billingAddressLine2, s.billingCity, s.billingState, s.billingPincode].filter(Boolean).join(', ') : '—',
          'Delivery Address': s.deliveryAddressLine1 ? [s.deliveryAddressLine1, s.deliveryAddressLine2, s.deliveryCity, s.deliveryState, s.deliveryPincode].filter(Boolean).join(', ') : '—',
          'Items & Qty': s.itemSummary || '—',
          'Total Quantity': s.totalQty || 0,
          'Taxable Amount': s.taxableAmount || 0,
          'Total Tax (GST)': s.totalTax || 0,
          'Net Bill': s.netBill || 0,
          'Paid Amount': s.paidAmount || 0,
          'Balance Due': s.balanceDue || 0,
          'Net Payable': s.netPayable || 0,
          'Status': (s.displayStatus || s.status || 'PENDING').toUpperCase()
        };
      });

      await exportToExcel(exportData, `Sales_Export_${new Date().toISOString().split('T')[0]}`);
      toast.success("Sales exported successfully");
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Failed to export sales");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500 relative">
      {/* Hidden Invoice for PDF */}
      {downloadingSale && (
        <div style={{ position: 'absolute', top: '-9999px', left: '-9999px', width: '800px', pointerEvents: 'none' }}>
          <InvoiceSale data={downloadingSale} settings={{ ...dynamicSettings, ...company }} />
        </div>
      )}

      {/* ── Isolated Filter Bar ─────────────────────────────────────────── */}
      <FilterSection
        search={localSearch}
        setSearch={setLocalSearch}
        filters={localFilters}
        setFilters={handleFilterChange}
        onAddClick={() => { setSelSale(null); setSaleOpen(true); }}
        addLabel="Sale"
        statusOptions={[]}
        //updated Halimunnisa 2026-06-08
        //Added an export button to the sales view that allows users to easily export their sales data to Excel. This feature enhances the usability of the sales management system by providing a convenient way for users to analyze and share their sales information outside of the application. The export functionality includes robust data mapping and formatting to ensure that the exported file is well-structured and user-friendly, making it easier for users to work with their sales data in Excel.
        onExport={handleExport}
        isExporting={isExporting}
      >
        {/* Sale Type Filter */}
        <div className="bg-slate-50/50 p-1 rounded-md border border-slate-200/60 shrink-0">
          <select
            className="text-[13px] font-medium bg-white border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-blue text-slate-600 w-36"
            value={localFilters.saleType || 'all'}
            onChange={e => handleFilterChange('saleType', e.target.value)}
          >
            <option value="all">All Types</option>
            <option value="retail">Retail Sales</option>
            <option value="project">Project Dispatch</option>
          </select>
        </div>
      </FilterSection>

      {/* ── Sales Table ─────────────────────────────────────────────────── */}
      <SalesTable
        sales={filteredSales}
        onDownload={handleDownload}
        onView={(s) => {
          if (s.recordType === 'material_request') setSelReq(s.originalRequest || s);
          else setSelReq(s);
        }}
        onEdit={(s) => {
          if (s.recordType === 'sale') { setSelSale(s); setSaleOpen(true); }
        }}
        onPay={(s) => {
          if (s.recordType === 'sale') setPaymentSale(s);
        }}
      />

      <SalePaymentModal
        isOpen={!!paymentSale}
        onClose={() => setPaymentSale(null)}
        sale={paymentSale}
        onSuccess={refreshAll}
      />

      {filteredSales.length === 0 && (
        <div className="card p-12 flex flex-col items-center justify-center bg-white shadow-sm border border-slate-100 rounded-2xl">
          <div className="w-16 h-16 bg-emerald-50 text-emerald-500 rounded-full flex items-center justify-center mb-4">
            <ShoppingBag size={32} />
          </div>
          <h2 className="text-xl font-bold text-brand-dark mb-1">No Sales Found</h2>
          <p className="text-slate-500 text-sm">
            {localSearch || Object.values(localFilters).some(Boolean)
              ? 'No results match your filters. Try clearing them.'
              : 'Sales and Project Dispatches will appear here.'}
          </p>
        </div>
      )}
    </div>
  );
};
